import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetMe,
  useUpdateMePreferences,
  getGetMeQueryKey,
} from "@workspace/api-client-react";

export const LOOKBACK_OPTIONS = [7, 14, 30, 90] as const;
export type LookbackDays = (typeof LOOKBACK_OPTIONS)[number];

const CHANNEL_NAME = "mailcraft:lookback-days";

export function useLookbackDays(): [LookbackDays, (d: LookbackDays) => void] {
  const queryClient = useQueryClient();
  const { data: me } = useGetMe();
  const updateMutation = useUpdateMePreferences();
  const channelRef = useRef<BroadcastChannel | null>(null);

  const serverDays: LookbackDays =
    me?.statsLookbackDays !== undefined &&
    (LOOKBACK_OPTIONS as readonly number[]).includes(me.statsLookbackDays)
      ? (me.statsLookbackDays as LookbackDays)
      : 7;

  const [optimistic, setOptimistic] = useState<LookbackDays | null>(null);
  const broadcastServerSnapshotRef = useRef<LookbackDays | null>(null);
  const serverDaysRef = useRef<LookbackDays>(serverDays);
  serverDaysRef.current = serverDays;

  const days = optimistic ?? serverDays;

  useEffect(() => {
    if (optimistic === null) return;
    if (broadcastServerSnapshotRef.current !== null) {
      if (serverDays !== broadcastServerSnapshotRef.current) {
        broadcastServerSnapshotRef.current = null;
        setOptimistic(null);
      }
    } else if (optimistic === serverDays) {
      setOptimistic(null);
    }
  }, [optimistic, serverDays]);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;

    const bc = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = bc;

    bc.onmessage = (event: MessageEvent<unknown>) => {
      const value = event.data;
      if (!(LOOKBACK_OPTIONS as readonly number[]).includes(value as number)) return;
      broadcastServerSnapshotRef.current = serverDaysRef.current;
      setOptimistic(value as LookbackDays);
      queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
    };

    return () => {
      bc.close();
      channelRef.current = null;
    };
  }, [queryClient]);

  const setDays = (d: LookbackDays) => {
    broadcastServerSnapshotRef.current = null;
    setOptimistic(d);
    updateMutation.mutate(
      { data: { statsLookbackDays: d } },
      {
        onSuccess: () => {
          channelRef.current?.postMessage(d);
          queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
          setOptimistic(null);
        },
        onError: () => {
          setOptimistic(null);
        },
      },
    );
  };

  return [days, setDays];
}
